import axios from "axios";
import {
  getPublicKey,
  encryptRequest,
  decryptResponse,
  isEncryptedResponse,
} from "../utils/hcCrypto";

// HC-services backend — SINGLE SOURCE OF TRUTH for the host. Everything (searches,
// case details, refresh/fetch-court-orders, court orders, and the order-PDF proxies)
// is served from here; the order-PDF fetchers import HC_API_HOST so there's one
// place to change to move backends.
//
// Currently the hc-service VM (jhc.jurident.com) running the merged hc-services-v2:
// encryption-enabled gateway + refresh + court orders + PDF proxies.
//
// The old Cloud Run service (hc-services-596718606544.asia-south1.run.app) was
// retired and deleted — it now 404s, so it is not a usable fallback.
export const HC_API_HOST = "https://jhc.jurident.com";
const API_BASE_URL = `${HC_API_HOST}/api`;

// MUST stay aligned with API_BASE_URL — `true` only when API_BASE_URL points at
// an encryption-enabled gateway.
const ENCRYPTION_ENABLED = true;

const JSON_HEADERS = { "Content-Type": "application/json" };

// Decrypt res.data in place if the gateway flagged the response as encrypted.
async function unwrapResponse(res, aesKey) {
  if (aesKey && isEncryptedResponse(res)) {
    return await decryptResponse(res.data, aesKey);
  }
  return res.data;
}

// Single POST helper used by every HC endpoint below. Builds an encrypted
// envelope when ENCRYPTION_ENABLED, falls back to plain JSON otherwise, and
// transparently decrypts both success and error response bodies so callers see
// the same shape they always did.
async function securePost(path, data, options = {}) {
  const url = `${API_BASE_URL}${path}`;
  const { headers: extraHeaders, ...restOptions } = options;
  const axiosOpts = {
    ...restOptions,
    headers: { ...JSON_HEADERS, ...(extraHeaders || {}) },
  };

  if (!ENCRYPTION_ENABLED) {
    const res = await axios.post(url, data, axiosOpts);
    return res.data;
  }

  const publicKey = await getPublicKey(API_BASE_URL);
  const { envelope, aesKey } = await encryptRequest(data, publicKey);

  try {
    const res = await axios.post(url, envelope, axiosOpts);
    return await unwrapResponse(res, aesKey);
  } catch (err) {
    // Mirror the encryption on error bodies so callers can still read err.response.data.code etc.
    if (err?.response && isEncryptedResponse(err.response)) {
      try {
        err.response.data = await decryptResponse(err.response.data, aesKey);
      } catch (_) {
        /* leave the encrypted body as-is if decrypt fails */
      }
    }
    throw err;
  }
}

// Same envelope as securePost, for the notification-preference endpoints which
// are PATCH. The gateway decrypts and re-encrypts regardless of method.
async function securePatch(path, data, options = {}) {
  const url = `${API_BASE_URL}${path}`;
  const { headers: extraHeaders, ...restOptions } = options;
  const axiosOpts = {
    ...restOptions,
    headers: { ...JSON_HEADERS, ...(extraHeaders || {}) },
  };

  if (!ENCRYPTION_ENABLED) {
    const res = await axios.patch(url, data, axiosOpts);
    return res.data;
  }

  const publicKey = await getPublicKey(API_BASE_URL);
  const { envelope, aesKey } = await encryptRequest(data, publicKey);

  try {
    const res = await axios.patch(url, envelope, axiosOpts);
    return await unwrapResponse(res, aesKey);
  } catch (err) {
    if (err?.response && isEncryptedResponse(err.response)) {
      try {
        err.response.data = await decryptResponse(err.response.data, aesKey);
      } catch (_) {
        /* leave the encrypted body as-is if decrypt fails */
      }
    }
    throw err;
  }
}

// Turn new-order notifications on/off for every case this user owns. Writes
// `notificationsEnabled` on the lawyers/client document.
export const setGlobalOrderNotifications = async (userId, enabled) =>
  await securePatch(`/notifications/global/${encodeURIComponent(userId)}`, {
    enabled,
  });

// Per-case override. Pass `null` to clear it so the case follows the global
// setting again.
export const setCaseOrderNotifications = async (caseId, enabled) =>
  await securePatch(`/notifications/case/${encodeURIComponent(caseId)}`, {
    enabled,
  });

export const apiRequest = async (endpoint, data, cookie) => {
  if (endpoint === "fetchBenches") {
    return await securePost(
      "/benches/highcourt",
      { ...data, appFlag: "web" },
      { withCredentials: true }
    );
  } else if (endpoint === "fetchCaptcha") {
    return await securePost("/captcha/highcourt", {});
  } else if (endpoint === "api/case") {
    return await securePost("/search/party-name", data);
  } else if (endpoint === "case/records") {
    return await securePost("/search/case-number", data);
  } else if (endpoint === "/case/details/highcourt") {
    try {
      return await securePost("/case/details/highcourt", data);
    } catch (err) {
      const errorData = err?.response?.data;
      if (errorData?.code === "HC_PORTAL_INVALID_RESPONSE") {
        return { error: "No data found from public sources", code: errorData.code };
      }
      throw err;
    }
  } else if (endpoint === "case/types") {
    return await securePost("/case/types", data);
  } else if (endpoint === "case/orders/pdf") {
    // Binary PDF stream — not a JSON body, so encryption does not apply.
    const { orderLink, hcservices_sessid, jsession_value } = data;
    const res = await axios.get(`${API_BASE_URL}/case/orders/highcourt/pdf`, {
      params: { orderLink, hcservices_sessid, jsession_value },
      responseType: "blob",
    });
    return res.data;
  } else if (endpoint === "search/filing-number") {
    return await securePost("/search/filing-number", data);
  } else if (endpoint === "search/advocate-name") {
    return await securePost("/search/advocate-name", data);
  } else if (endpoint === "search/cnr-number") {
    return await securePost("/search/cnr-number", data);
  } else if (endpoint === "search/case-type") {
    return await securePost("/search/case-type", data);
  } else if (endpoint === "court-orders/party-name") {
    return await securePost("/court-orders/party-name", data);
  } else if (endpoint === "court-orders/case-number") {
    return await securePost("/court-orders/case-number", data);
  } else if (endpoint === "court-orders/order-date") {
    return await securePost("/court-orders/order-date", data);
  } else if (endpoint === "fetch-court-orders") {
    // Autonomous Court Orders scrape (backend solves the CAPTCHA itself).
    // Body: { court_code, state_code, court_complex_code, caseStatusSearchType,
    //         case_type_order, case_no_order, rgyearCaseOrder }
    return await securePost("/fetch-court-orders", data);
  } else if (endpoint === "court-orders/pdf") {
    // Binary PDF stream — not a JSON body, so encryption does not apply.
    // display_pdf.php needs the full lookup tuple (filename/caseno/cCode/cino/
    // state_code) plus both session cookies (the portal gates the PDF behind the
    // HCSERVICES_SESSID + JSESSION that ran the search).
    const {
      orderurlpath, caseno, cCode, cino, state_code, flag,
      hcservices_sessid, jsession_value, sessionId,
    } = data;
    const res = await axios.get(`${API_BASE_URL}/court-orders/pdf`, {
      params: {
        orderurlpath, caseno, cCode, cino, state_code, flag,
        hcservices_sessid, jsession_value, sessionId,
      },
      responseType: "blob",
    });
    return res.data;
  } else if (endpoint === "cause-list") {
    return await securePost("/cause-list", data);
  }
};
